import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { GetAllProductsWithImages } from "../../services/ApiServices/ProductService";
import { Breadcrumb } from "../Breadcrumb";
import ProductCard from "../ProductCard";
import "../../styles/products.css";
import { SortByBar } from "../SortByBar";
import { FaSpinner } from "react-icons/fa";

type ProductProps = {
    loading: boolean;
    types: string[];
    setLoading: (loading: boolean) => void;
};

type Product = {
    id: string;
    name: string;
    price: number;
    description: string;
    productImages: ProductImage[];
    type: string;
    status: string;
};

type ProductImage = {
    imageUrl: string;
    productId: string;
};

function Products({ types, loading, setLoading }: ProductProps) {
    const [products, setProducts] = useState<Product[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [filterPrice, setFilterPrice] = useState<number | null>(null);
    const [filteredRooms, setFilteredRooms] = useState<Product[]>([]);

    const location = useLocation();

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const productsResponse = await GetAllProductsWithImages();
            const activeProducts = productsResponse.filter((product: Product) => product.status === 'Active');
            setProducts(activeProducts);
            filterProducts(activeProducts);
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An error occurred. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterProducts = (products: Product[]) => {
        let filteredProducts = products;
        if (types.length > 0 && types[0] !== '') {
            filteredProducts = products.filter((product: Product) => types.includes(product.type));
        }
        setFilteredRooms(filterProductsByPrice(filteredProducts, filterPrice));
    };

    const filterProductsByPrice = (products: Product[], filterPrice: number | null) => {
        if (!filterPrice) {
            return products;
        }
        return products.filter(product => product.price <= filterPrice);
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const query = queryParams.get('query');
        let filteredProducts = products;

        if (types.length > 0 && types[0] !== '') {
            filteredProducts = products.filter((product: Product) => types.includes(product.type));
        }

        if (query) {
            filteredProducts = filteredProducts.filter((product: Product) =>
                product.name.toLowerCase().includes(query.toLowerCase())
            );
            setFilteredRooms(filteredProducts);
        } else {
            setFilteredRooms(filterProductsByPrice(filteredProducts, filterPrice));
        }
    }, [location.search, products, filterPrice, types]);

    return (
        <div className="w-full p-4">
            <SortByBar products={products} filteredProducts={filteredRooms} setFilteredProducts={setFilteredRooms} />
            <div>{errorMessage}</div>
            <div>
                {filteredRooms.length === 0 && (
                    <div className="spinner-container">
                        <FaSpinner className="spinner" />
                        <h2>Xin lỗi vì sự bất tiện này!</h2>
                        <p>Hãy đợi vài giây để chúng tôi tải lại dữ liệu. Nếu vẫn không tải được, vui lòng thử lại.</p>
                    </div>
                )}
            </div>
            <div className="products-wrapper">
                {filteredRooms.map((product: Product) => (
                    <ProductCard
                        key={product.id}
                        id={product.id}
                        name={product.name}
                        price={product.price}
                        images={product.productImages}
                    />
                ))}
            </div>
        </div>
    );
}

export default Products;
